import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { BrandLogo } from "../components/BrandLogo";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register, loading } = useAuth();
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", role: searchParams.get("role") || "seeker",
    phone: "", organization: "", agency_code: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const user = isRegister ? await register(form) : await login(form.email, form.password);
      toast.success(isRegister ? "Welcome to Safe Harbor" : "Welcome back");
      // All vettable roles must complete the application before reaching the dashboard.
      const vettableRoles = ["provider", "agency_owner", "agency_staff", "case_manager", "referral_partner_pro"];
      if (isRegister && vettableRoles.includes(user.role)) {
        navigate("/provider/application");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    }
  };

  return (
    <div data-testid={`auth-${mode}`} className="max-w-7xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div className="hidden lg:block">
        <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-3">
          {isRegister ? "Join the network" : "Welcome back"}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0A192F] leading-tight">
          {isRegister ? "Create your Safe Harbor account." : "Sign in to Safe Harbor."}
        </h1>
        <p className="mt-5 text-[#4A5568] text-lg max-w-md">
          {isRegister
            ? "Free for housing seekers. Subscription plans for providers and referral professionals."
            : "Continue placing, listing, and referring."}
        </p>
        <div className="mt-10 border-t border-[#D1D5DB] pt-8 text-sm text-[#4A5568]">
          By creating an account you agree to our community standards. Safe Harbor is not a housing provider — it is a placement and referral network.
        </div>
      </div>

      <div className="bg-white border border-[#D1D5DB] p-8 sm:p-10 rounded-sm">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-sm bg-white border border-[#D1D5DB] flex items-center justify-center overflow-hidden">
            <BrandLogo className="w-8 h-8 object-contain" />
          </span>
          <span className="font-heading font-bold text-[#0A192F]">Safe Harbor Veterans</span>
        </div>
        <h2 className="font-heading text-2xl font-bold text-[#0A192F]">{isRegister ? "Create account" : "Sign in"}</h2>
        <form onSubmit={submit} className="mt-6 space-y-4">
          {isRegister && (
            <>
              <div>
                <Label>I am a</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="role-select" className="border-[#D1D5DB]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seeker">Housing Seeker</SelectItem>
                    <SelectItem value="provider">Housing Provider</SelectItem>
                    <SelectItem value="case_manager">Case Manager (Independent)</SelectItem>
                    <SelectItem value="agency_owner">Agency Owner</SelectItem>
                    <SelectItem value="agency_staff">Agency Staff (with code)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="full_name">Full name</Label>
                <Input id="full_name" data-testid="full-name-input" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
              </div>
              {(form.role === "provider" || form.role === "case_manager" || form.role === "agency_owner") && (
                <div>
                  <Label>Organization</Label>
                  <Input data-testid="org-input" value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="e.g. Harbor House Recovery" />
                </div>
              )}
              {form.role === "agency_staff" && (
                <div>
                  <Label>Agency code</Label>
                  <Input data-testid="agency-code-input" value={form.agency_code} onChange={(e) => set("agency_code", e.target.value)} placeholder="Provided by your agency owner" />
                </div>
              )}
              <div>
                <Label>Phone (optional)</Label>
                <Input data-testid="phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
            </>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="password-input" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} />
          </div>
          <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full bg-[#1E3A8A] hover:bg-[#1A365D] text-white py-6 rounded-md">
            {loading ? "Working..." : (isRegister ? "Create Account" : "Sign In")}
          </Button>
        </form>
        <div className="mt-6 text-sm text-[#4A5568] text-center">
          {isRegister ? (
            <>Already have an account? <Link to="/login" className="text-[#1E3A8A] font-medium" data-testid="link-login">Sign in</Link></>
          ) : (
            <>New to Safe Harbor? <Link to="/register" className="text-[#1E3A8A] font-medium" data-testid="link-register">Create an account</Link></>
          )}
        </div>
      </div>
    </div>
  );
}
